// src/components/ClientDetailCard.jsx
// Simplified client detail card - Documents, Notes, Communication only
// Health data, plan history, and insurance info now in dedicated components

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { listClientUploads, uploadClientFile, deleteClientUpload, getUploadDownloadUrl } from "../api";
import { Upload, Trash2, Download, FileText, Calendar } from "lucide-react";
import { formatDate } from "../utils/formatters";
import RecentCommunication from "./RecentCommunication";

export default function ClientDetailCard({ client, onClientUpdate, onViewComms, onEdit }) {
  const { t } = useTranslation();
  const [uploads, setUploads] = useState(client?.uploads || []);
  const [loadingUp, setLoadingUp] = useState(false);
  const fileRef = useRef(null);
  const [label, setLabel] = useState("SOA");

  useEffect(() => {
    let live = true;
    async function load() {
      try {
        if (!client?.id) return;
        const rows = await listClientUploads(client.id);
        if (live) setUploads(rows);
      } catch {}
    }
    load();
    return () => { live = false; };
  }, [client?.id]);

  async function onPick() {
    fileRef.current?.click();
  }

  async function onFileChange(e) {
    const f = e.target.files?.[0];
    if (!f || !client?.id) return;
    try {
      setLoadingUp(true);
      const created = await uploadClientFile(client.id, f, label || t('document'));
      setUploads(prev => [created, ...prev]);
    } catch (err) {
      alert(t('uploadFailed'));
    } finally {
      setLoadingUp(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function onDelete(u) {
    if (!client?.id) return;
    if (!confirm(`${t('delete')} ${u.originalName || u.file}?`)) return;
    try {
      await deleteClientUpload(client.id, u.id);
      setUploads(prev => prev.filter(x => x.id !== u.id));
    } catch {
      alert(t('deleteFailed'));
    }
  }

  return (
    <div className="space-y-6">
      {/* Notes Card */}
      {client.notes && (
        <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow">
          <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-4 border-b border-slate-100">
            <h3 className="font-bold text-lg text-[#172A3A] flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-yellow-500 flex items-center justify-center text-white">
                <FileText size={18} />
              </span>
              {t('notes')}
            </h3>
          </div>
          <div className="p-6">
            <div className="text-sm text-slate-700 bg-slate-50 rounded-xl p-4 border border-slate-200 leading-relaxed whitespace-pre-wrap">
              {client.notes}
            </div>
          </div>
        </div>
      )}

      {/* Recent Communication */}
      <RecentCommunication clientId={client?.id} onViewAll={onViewComms} />

      {/* Documents & Uploads Card */}
      <div className="bg-white rounded-2xl shadow-lg border border-slate-100 overflow-hidden hover:shadow-xl transition-shadow">
        <div className="bg-gradient-to-r from-slate-50 to-gray-50 p-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-lg text-[#172A3A] flex items-center gap-2">
              <span className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-500 to-gray-600 flex items-center justify-center text-white">
                <Upload size={18} />
              </span>
              {t('documents')}
            </h3>
            {/* Upload control */}
            <div className="flex items-center gap-2">
              <select
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white hover:border-purple-400 transition-colors focus:ring-2 focus:ring-purple-200 focus:border-purple-400 outline-none"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                title="Label for the upload"
              >
                <option value="SOA">{t('soa')}</option>
                <option value="PTC">{t('ptc')}</option>
                <option value="Enrollment">{t('enrollment')}</option>
                <option value="Other">{t('other')}</option>
              </select>
              <button
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:from-purple-600 hover:to-pink-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-md hover:shadow-lg transform hover:scale-105"
                onClick={onPick}
                disabled={loadingUp}
                title="Upload a document"
              >
                <Upload size={16} /> {loadingUp ? t('uploading') : t('addDocument')}
              </button>
              <input ref={fileRef} type="file" className="hidden" onChange={onFileChange} />
            </div>
          </div>
        </div>
        <div className="p-6">
          {uploads.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
              <Upload size={48} className="mx-auto text-slate-300 mb-3" />
              <p className="text-sm text-slate-500 font-medium">{t('noDocuments')}</p>
              <p className="text-xs text-slate-400 mt-1">{t('clickToUpload')}</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {uploads.map((u) => (
                <div key={u.id} className="flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-xl border border-slate-200 transition-all group">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                      {(u.label || "DOC").substring(0, 3).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{u.originalName || u.file}</div>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                        <span className="flex items-center gap-1">
                          <FileText size={12} />
                          {u.label || t('document')}
                        </span>
                        <span>•</span>
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {formatDate(u.date)}
                        </span>
                        <span>•</span>
                        <span>{formatSize(u.size)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <a
                      href={getUploadDownloadUrl(u.file)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 hover:border-blue-400 hover:bg-blue-50 text-sm font-medium text-slate-700 hover:text-blue-600 transition-all"
                      title={t('download')}
                      target="_blank" rel="noreferrer"
                    >
                      <Download size={14} /> {t('download')}
                    </a>
                    <button
                      onClick={() => onDelete(u)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 hover:border-red-400 hover:bg-red-50 text-sm font-medium text-red-600 hover:text-red-700 transition-all"
                      title={t('delete')}
                    >
                      <Trash2 size={14} /> {t('delete')}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function formatSize(n) {
  if (!n && n !== 0) return "—";
  const kb = n / 1024, mb = kb / 1024;
  if (mb >= 1) return `${mb.toFixed(2)} MB`;
  if (kb >= 1) return `${kb.toFixed(1)} KB`;
  return `${n} B`;
}
